import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import LugarAutocomplete from "@/components/LugarAutocomplete";
import { canAccessModule } from "@/utils/permissions";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";
import "./cabina.css";

const fmt = (v) => (v == null ? "—" : `$${Math.round(Number(v)).toLocaleString("es-MX")}`);
const fmtDec = (v) => (v == null ? "—" : `$${Number(v).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const I = {
  open: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18" /><path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" /><path d="M6 6l1 14a2 2 0 002 2h6a2 2 0 002-2l1-14" /></svg>,
  edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4 12.5-12.5z" /></svg>,
  send: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13" /><path d="M22 2l-7 20-4-9-9-4 20-7z" /></svg>,
  wallet: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12V8a2 2 0 00-2-2H5a2 2 0 000 4h15a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2V6" /></svg>,
  dollar: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" /></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>,
  arrow: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>,
};

const estatusChip = (s) => {
  const map = { pendiente: ["c-pend", "Sin costo"], capturado: ["c-capt", "Capturado"], validado: ["c-val", "Validado"] };
  const [cls, l] = map[s] || ["c-pend", s];
  return <span className={`chip ${cls}`}>{l}</span>;
};

const ETAPAS = [
  ["por_validar_costos", "Por validar costos"],
  ["listo_para_facturar", "Listo para facturar"],
  ["factura_solicitada", "Factura solicitada"],
  ["facturado", "Facturado"],
];

export default function CabinaExpediente() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [cat, setCat] = useState(null);
  const [tab, setTab] = useState("servicios");
  const [showAddServ, setShowAddServ] = useState(false);
  const [servCostoOpen, setServCostoOpen] = useState(null);
  const [showFactura, setShowFactura] = useState(false);
  const [showPago, setShowPago] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [expandedFac, setExpandedFac] = useState(null);

  const canCosto = canAccessModule(user, "cabina_costos");
  const canFiscal = canAccessModule(user, "cabina_matriz_fiscal") || canCosto;
  const canVerMargen = canAccessModule(user, "cabina_ver_margenes");

  const load = useCallback(async () => {
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/cabina/expedientes/${id}`),
        cat ? Promise.resolve({ data: cat }) : axios.get(`${API}/cabina/catalogo`),
      ]);
      setData(r1.data);
      if (!cat) setCat(r2.data);
    } catch (e) { toast.error("Error cargando expediente"); nav("/cabina-ventas"); }
  }, [id, cat, nav]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="cv"><p style={{ color: "var(--cv-muted)" }}>Cargando…</p></div>;

  const { expediente: e, servicios, bitacora } = data;
  const t = e.totales || {};
  const etapaIdx = ETAPAS.findIndex(([k]) => k === e.etapa);
  const facturado = e.etapa === "facturado";

  const solicitarFactura = async () => {
    const nota = prompt("Nota para administración (opcional):") || "";
    try { await axios.post(`${API}/cabina/expedientes/${e.id}/solicitar-factura`, { nota }); toast.success("Factura solicitada"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const borrarServicio = async (s) => {
    if (!confirm(`¿Borrar servicio ${s.folio}?`)) return;
    try { await axios.delete(`${API}/cabina/servicios/${s.id}`); toast.success("Servicio borrado"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const borrarFactura = async (f) => {
    const motivo = prompt(`Borrar factura ${f.folio || "s/f"}. Motivo:`);
    if (!motivo) return;
    try { await axios.delete(`${API}/cabina/facturas/${f.id}`, { params: { motivo } }); toast.success("Factura borrada"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const borrarPago = async (f, p) => {
    const motivo = prompt("Borrar pago. Motivo:");
    if (!motivo) return;
    try { await axios.delete(`${API}/cabina/facturas/${f.id}/pagos/${p.id}`, { params: { motivo } }); toast.success("Pago borrado"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const cancelado = e.situacion === "cancelado";
  const isAdmin = user?.role === "admin";

  const reactivar = async () => {
    const motivo = window.prompt("Motivo de la reactivación (obligatorio, mínimo 3 caracteres):", "");
    if (!motivo || motivo.trim().length < 3) return;
    try {
      await axios.post(`${API}/cabina/expedientes/${e.id}/reactivar`, null, { params: { motivo } });
      toast.success("Expediente reactivado");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al reactivar"); }
  };

  const revertirEtapa = async () => {
    const motivo = window.prompt("Motivo para revertir el estatus (obligatorio, mínimo 3 caracteres):", "");
    if (!motivo || motivo.trim().length < 3) return;
    if (!window.confirm(`¿Confirmas revertir la etapa de este expediente? El vendedor podrá volver a hacer cambios.`)) return;
    try {
      const r = await axios.post(`${API}/cabina/expedientes/${e.id}/revertir-etapa`, null, { params: { motivo } });
      toast.success(`Etapa revertida a: ${r.data.etapa}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al revertir"); }
  };

  return (
    <div className="cv" data-testid="cabina-expediente">
      <button className="back" onClick={() => nav("/cabina-ventas")}>{I.arrow} Cabina</button>

      {cancelado && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "12px 16px", marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }} data-testid="banner-cancelado">
          <div>
            <b style={{ color: "#991b1b", fontSize: 13.5 }}>Expediente cancelado</b>
            <div style={{ color: "#7f1d1d", fontSize: 12, marginTop: 2 }}>
              {e.cancelado_at ? `Cancelado el ${(e.cancelado_at || "").slice(0, 10)}` : ""}
              {e.cancelado_motivo ? ` · Motivo: ${e.cancelado_motivo}` : ""}
              {" · No cuenta en dashboards ni tableros."}
            </div>
          </div>
          {isAdmin && (
            <button className="btn dark sm" onClick={reactivar} data-testid="btn-reactivar-expediente">
              Reactivar expediente
            </button>
          )}
        </div>
      )}

      <div className="dethead">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div className="folio">{e.folio}</div>
            <h2>{e.canal === "groups" && e.proyecto_nombre ? e.proyecto_nombre : e.cliente_nombre}</h2>
            <div style={{ display: "flex", gap: 7, alignItems: "center", flexWrap: "wrap" }}>
              <span className="chip c-canal">{e.canal}</span>
              <span className="chip c-canal">{e.subtipo}</span>
              {cancelado ? (
                <span className="chip" style={{ background: "#fee2e2", color: "#991b1b" }}>Cancelado</span>
              ) : (
                <span className={`chip ${e.etapa === "facturado" ? "c-fact" : e.etapa === "factura_solicitada" ? "c-sol" : e.etapa === "listo_para_facturar" ? "c-val" : "c-pend"}`}>
                  {e.etapa === "facturado" && !t.facturado_completo ? "Facturado parcial" : ETAPAS.find(([k]) => k === e.etapa)?.[1]}
                </span>
              )}
              <span className="chip c-pend">{t.servicios_validados || 0}/{t.servicios_totales || 0} costos validados</span>
              {e.canal === "groups" && e.proyecto_nombre && <span style={{ color: "var(--cv-muted)", fontSize: 12.5 }}>· Cliente: {e.cliente_nombre}</span>}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {!cancelado && (e.etapa === "por_validar_costos" || e.etapa === "listo_para_facturar") && <button className="btn ghost sm" onClick={solicitarFactura} data-testid="btn-solicitar-factura">{I.send} Solicitar factura</button>}
            {!cancelado && canFiscal && (e.etapa === "listo_para_facturar" || e.etapa === "factura_solicitada" || (facturado && !t.facturado_completo)) && <button className="btn dark sm" onClick={() => setShowFactura(true)} data-testid="btn-registrar-factura">{facturado && !t.facturado_completo ? "Facturar el resto" : "Registrar factura"}</button>}
            {/* Editar expediente: vendedor sólo antes de factura solicitada; admin siempre (mientras no esté cancelado y no esté 100% cobrado) */}
            {!cancelado && (isAdmin ? !t.facturado_completo : (e.etapa === "por_validar_costos" || e.etapa === "listo_para_facturar")) &&
              <button className="btn ghost sm" onClick={() => setShowEdit(true)} data-testid="btn-editar-expediente">{I.edit} Editar</button>}
            {/* Revertir etapa: sólo admin, sólo si etapa avanzada */}
            {!cancelado && isAdmin && (e.etapa === "factura_solicitada" || e.etapa === "listo_para_facturar" || (e.etapa === "facturado" && !((e.facturacion || {}).facturas || []).length)) &&
              <button className="btn ghost sm" style={{ color: "var(--cv-night)", borderColor: "#fca5a5" }} onClick={revertirEtapa} data-testid="btn-revertir-etapa">↩ Revertir estatus</button>}
          </div>
        </div>
        <div className="etapas">
          {ETAPAS.map(([k, l], i) => (
            <div key={k} className={`etapa ${i < etapaIdx ? "done" : ""} ${i === etapaIdx ? "on" : ""}`}>
              <i /> {l}
            </div>
          ))}
        </div>
        <div className="meta">
          <div><b>Venta</b><span className="mono">{fmt(t.venta_bruta)}</span></div>
          <div><b>Subtotal {facturado ? "facturado" : "estimado"}</b><span className="mono">{fmt(facturado ? t.subtotal_facturado : t.subtotal_estimado)}</span></div>
          <div><b>IVA {facturado ? "facturado" : "estimado"}</b><span className="mono">{fmt(facturado ? t.iva_facturado : t.iva_estimado)}</span></div>
          {canVerMargen && <div><b>Utilidad {facturado ? "real" : "estimada"}</b><span className="mono" style={{ color: "var(--cv-blue)" }}>{fmt(facturado ? t.utilidad_real : t.utilidad_estimada)}</span></div>}
          {canVerMargen && <div><b>Margen</b><span className="mono">{(facturado ? t.margen_real : t.margen_estimado) ?? "—"}%</span></div>}
          <div><b>Agente</b><span>{e.agente_nombre || "—"}</span></div>
          <div><b>RFC</b><span>{e.cliente_rfc || "—"}</span></div>
          <div><b>Registrado</b><span>{(e.registro || "").slice(0, 10)}</span></div>
        </div>
      </div>

      <div className="cv-subtabs">
        <button className={tab === "servicios" ? "on" : ""} onClick={() => setTab("servicios")} data-testid="tab-servicios">Servicios <span className="badge">{servicios.length}</span></button>
        <button className={tab === "facturacion" ? "on" : ""} onClick={() => setTab("facturacion")} data-testid="tab-facturacion">Facturación <span className="badge">{(e.facturacion?.facturas || []).length}</span></button>
        <button className={tab === "bitacora" ? "on" : ""} onClick={() => setTab("bitacora")} data-testid="tab-bitacora">Bitácora <span className="badge">{bitacora.length}</span></button>
      </div>

      {tab === "servicios" && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <p style={{ color: "var(--cv-muted)", fontSize: 12.5, margin: 0 }}>Cada servicio se costea individualmente. Al validar los costos el expediente pasa a &quot;Listo para facturar&quot;.</p>
            {!facturado && <button className="btn" onClick={() => setShowAddServ(true)} data-testid="btn-agregar-servicio">+ Agregar servicio</button>}
          </div>
          <div className="card">
            <div className="tw">
              <table className="dense">
                <thead><tr>
                  <th>Servicio</th>
                  <th className="nw">Localizador</th>
                  <th className="nw">Ámbito</th>
                  <th>Proveedor</th>
                  <th className="r nw">Venta</th>
                  <th className="r nw">Costo</th>
                  {canVerMargen && <th className="r nw">Utilidad</th>}
                  {canVerMargen && <th className="r nw">Margen</th>}
                  <th className="nw">Estatus</th>
                  <th className="r nw" style={{ width: "1%" }} />
                </tr></thead>
                <tbody>
                  {servicios.length === 0 && <tr><td colSpan={canVerMargen ? 10 : 8} className="empty"><b>Sin servicios</b>Agrega el primero.</td></tr>}
                  {(() => {
                    // Agrupar servicios por grupo de categoría (TRA, HOS, AYB, ...)
                    // Con fallback vía catálogo si el servicio no trae grupo_categoria
                    const gruposCatalog = cat?.grupos_categoria || [];
                    const gruposMap = Object.fromEntries(gruposCatalog.map((g) => [g.codigo, g]));
                    const catByCode = Object.fromEntries((cat?.categorias || []).map((c) => [c.codigo, c]));
                    const grupoOrden = (code) => (gruposMap[code]?.orden ?? 999);
                    const grupoNombre = (code) => (gruposMap[code]?.nombre || (code ? `Grupo ${code}` : "Sin grupo"));

                    const grupos = [];
                    const idx = {};
                    servicios.forEach((s) => {
                      const key = s.grupo_categoria || catByCode[s.categoria_codigo]?.grupo || "SIN";
                      if (idx[key] === undefined) { idx[key] = grupos.length; grupos.push({ key, nombre: grupoNombre(key), items: [] }); }
                      grupos[idx[key]].items.push(s);
                    });
                    grupos.sort((a, b) => grupoOrden(a.key) - grupoOrden(b.key));

                    const colSpan = canVerMargen ? 10 : 8;
                    return grupos.flatMap((g) => {
                      const gVenta = g.items.reduce((a, s) => a + (s.venta_total_mxn || 0), 0);
                      const gCosto = g.items.reduce((a, s) => a + (s.costo_total_mxn || 0), 0);
                      const gUtl = g.items.reduce((a, s) => a + (s.fiscal?.utilidad_total || 0), 0);
                      const validados = g.items.filter((s) => s.estatus_costo === "validado").length;
                      return [
                        <tr key={`grp-${g.key}`} className="grp-row" data-testid={`serv-grupo-${g.key}`}>
                          <td colSpan={colSpan} className="grp-cell">
                            <div className="grp-header">
                              <div className="grp-title">
                                <span className="grp-code">{g.key}</span>
                                <span className="grp-name">{g.nombre}</span>
                                <span className="grp-count">{g.items.length} servicio{g.items.length !== 1 ? "s" : ""}</span>
                                <span className="grp-val">{validados}/{g.items.length} validados</span>
                              </div>
                              <div className="grp-totales">
                                <span><b>Venta</b> <span className="mono">{fmt(gVenta)}</span></span>
                                <span><b>Costo</b> <span className="mono">{fmt(gCosto)}</span></span>
                                {canVerMargen && <span><b>Utilidad</b> <span className="mono" style={{ color: "var(--cv-blue)" }}>{fmt(gUtl)}</span></span>}
                              </div>
                            </div>
                          </td>
                        </tr>,
                        ...g.items.map((s) => (
                          <tr key={s.id}>
                            <td>{s.categoria_nombre}<div className="s">{s.descripcion || s.folio}</div></td>
                            <td>{s.localizador ? <span className="loc">{s.localizador}</span> : <span style={{ color: "var(--cv-ghost)" }}>—</span>}</td>
                            <td className="nw"><span className="s" style={{ margin: 0 }}>{s.ambito_fiscal}</span></td>
                            <td>{s.proveedor_nombre || "—"}</td>
                            <td className="r nw mono">{fmt(s.venta_total_mxn)}</td>
                            <td className="r nw mono">{fmt(s.costo_total_mxn)}<div className="s">{(s.fiscal?.origen_costo || "").replace(/_/g, " ")}</div></td>
                            {canVerMargen && <td className="r nw mono" style={{ color: "var(--cv-blue)" }}>{fmt(s.fiscal?.utilidad_total)}</td>}
                            {canVerMargen && <td className="r nw mono">{s.fiscal?.margen_pct ?? "—"}%</td>}
                            <td>{estatusChip(s.estatus_costo)}</td>
                            <td>
                              <div className="ibs">
                                {/* Botón capturar/validar costo:
                                    - Regular: sólo si etapa permite edición (antes de solicitar factura) y no está validado
                                    - Admin: puede editar/validar en cualquier momento (incluso facturado / factura_solicitada) */}
                                {canCosto && s.estatus_costo !== "validado" && (isAdmin || (e.etapa !== "facturado" && e.etapa !== "factura_solicitada")) &&
                                  <button className="ib pri" title="Capturar/validar costo" onClick={() => setServCostoOpen(s)} data-testid={`ib-costo-${s.folio}`}>{I.dollar}</button>}
                                {/* Admin puede reeditar costo validado también */}
                                {isAdmin && s.estatus_costo === "validado" && !facturado &&
                                  <button className="ib" title="Editar costo (admin)" onClick={() => setServCostoOpen(s)} data-testid={`ib-costo-adm-${s.folio}`}>{I.edit}</button>}
                                {/* Borrar servicio: sólo antes de facturar, o admin cuando facturado */}
                                {(!facturado || isAdmin) &&
                                  <button className="ib danger" title="Borrar servicio" onClick={() => borrarServicio(s)} data-testid={`ib-del-serv-${s.folio}`}>{I.trash}</button>}
                              </div>
                            </td>
                          </tr>
                        ))
                      ];
                    });
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {tab === "facturacion" && (
        <>
          <div className="card">
            <div className="card-b">
              <div className="kpis">
                <Kpi k="Facturado" v={fmt(t.facturado)} />
                <Kpi k="Cobrado" v={fmt(t.cobrado)} />
                <Kpi k="Por cobrar" v={fmt(t.por_cobrar)} dark />
                <Kpi k="Por facturar" v={fmt(t.por_facturar)} mut />
              </div>
            </div>
          </div>

          <div className="card" style={{ marginTop: 14 }}>
            <div className="tw">
              <table className="dense">
                <thead><tr>
                  <th className="nw">Folio ERP</th>
                  <th className="nw">UUID</th>
                  <th className="nw">Fecha</th>
                  <th className="r nw">Subtotal</th>
                  <th className="r nw">IVA</th>
                  <th className="r nw">Total</th>
                  <th className="nw">Método</th>
                  <th className="nw">Forma</th>
                  <th className="r nw">Cobrado</th>
                  <th className="r nw">Saldo</th>
                  <th className="r nw" style={{ width: "1%" }} />
                </tr></thead>
                <tbody>
                  {((e.facturacion || {}).facturas || []).length === 0 && <tr><td colSpan={11} className="empty"><b>Sin CFDIs</b></td></tr>}
                  {((e.facturacion || {}).facturas || []).map((f) => {
                    const cobrado = (f.pagos || []).reduce((a, p) => a + p.importe, 0);
                    const saldo = f.total - cobrado;
                    const exp = expandedFac === f.id;
                    return (
                      <>
                        <tr key={f.id} className="click" onClick={() => setExpandedFac(exp ? null : f.id)}>
                          <td className="nw mono">
                            <b style={{ color: "var(--cv-blue)" }}>{f.folio || "s/f"}</b>
                            {f.es_global && (
                              <span className="chip c-sol" style={{ marginLeft: 6, fontSize: 9.5, padding: "1px 5px" }} title="Factura global agrupa varios expedientes">Global</span>
                            )}
                          </td>
                          <td className="nw mono" style={{ fontSize: 10.5, color: "var(--cv-ghost)", maxWidth: 130, overflow: "hidden", textOverflow: "ellipsis" }} title={f.uuid || ""}>{f.uuid || "—"}</td>
                          <td className="nw mono">{f.fecha}</td>
                          <td className="r nw mono">{fmt(f.subtotal)}</td>
                          <td className="r nw mono">{fmt(f.iva)}</td>
                          <td className="r nw mono"><b>{fmt(f.total)}</b></td>
                          <td><span className={`chip ${f.metodo === "PUE" ? "c-val" : "c-warn"}`}>{f.metodo}</span></td>
                          <td className="mono nw">{f.forma}</td>
                          <td className="r nw mono">{fmt(cobrado)}</td>
                          <td className="r nw mono" style={{ color: saldo > 0.5 ? "var(--cv-night)" : "var(--cv-forest)" }}>{fmt(saldo)}</td>
                          <td onClick={(ev) => ev.stopPropagation()}>
                            <div className="ibs">
                              {saldo > 0.5 && canFiscal && !f.es_global && <button className="ib pri" title="Registrar pago" onClick={() => setShowPago(f)} data-testid={`ib-pago-${f.folio}`}>{I.wallet}</button>}
                              {saldo > 0.5 && f.es_global && (
                                <span title="Los pagos de esta factura global se aplican desde Administración → Pagos Globales" style={{ fontSize: 10.5, color: "var(--cv-muted)", padding: "0 6px" }}>Pagos globales</span>
                              )}
                              {user?.role === "admin" && !f.es_global && (
                                <button
                                  className="ib danger"
                                  title="Borrar factura (solo admin)"
                                  onClick={() => borrarFactura(f)}
                                  data-testid={`ib-del-fac-${f.folio}`}
                                >{I.trash}</button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {exp && (f.pagos || []).length > 0 && (
                          <tr className="sub-row">
                            <td colSpan={11}>
                              <div className="pagos">
                                {(f.pagos || []).map((p) => (
                                  <span key={p.id} className="pago">
                                    {p.fecha} · <b>{fmt(p.importe)}</b> · forma {p.forma}
                                    {canFiscal && <button className="xp" title="Borrar pago" onClick={(ev) => { ev.stopPropagation(); borrarPago(f, p); }} data-testid={`xp-pago-${p.id}`}>×</button>}
                                  </span>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {canVerMargen && t.facturado_completo && (
            <div className="card" style={{ marginTop: 16 }}>
              <div className="card-h"><h3>Utilidad real (post-facturación)</h3><span className="sub">números en firme</span></div>
              <div className="card-b">
                <div className="fout">
                  <div><div className="k">Subtotal en firme</div><div className="v hi">{fmt(t.subtotal_en_firme)}</div></div>
                  <div><div className="k">Utilidad real</div><div className="v hi">{fmt(t.utilidad_real)}</div></div>
                  <div><div className="k">Margen real</div><div className="v hi">{t.margen_real ?? 0}%</div></div>
                  <div><div className="k">IVA por pagar</div><div className="v">{fmt(t.iva_por_pagar_real)}</div></div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {tab === "bitacora" && (
        <div className="card">
          <ul className="log">
            {bitacora.length === 0 && <li style={{ display: "block", color: "var(--cv-muted)", textAlign: "center", padding: 30 }}>Sin registros</li>}
            {bitacora.map((b) => (
              <li key={b.id}>
                <span className="cuando">{new Date(b.ts).toLocaleString("es-MX")}</span>
                <span className="quien">{b.usuario_nombre}</span>
                <span className="que"><b>{b.accion_texto}</b>{b.detalle && <span className="dif">{b.detalle}</span>}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {showAddServ && <AddServicioDialog exp={e} cat={cat} onClose={() => setShowAddServ(false)} onSaved={() => { setShowAddServ(false); load(); }} />}
      {showEdit && <EditarExpedienteDialog exp={e} cat={cat} onClose={() => setShowEdit(false)} onSaved={() => { setShowEdit(false); load(); }} />}
      {servCostoOpen && <CapturaCostoDialog serv={servCostoOpen} cat={cat} onClose={() => setServCostoOpen(null)} onSaved={() => { setServCostoOpen(null); load(); }} />}
      {showFactura && <RegistrarFacturaDialog exp={e} cat={cat} onClose={() => setShowFactura(false)} onSaved={() => { setShowFactura(false); load(); }} />}
      {showPago && <RegistrarPagoDialog factura={showPago} cat={cat} onClose={() => setShowPago(null)} onSaved={() => { setShowPago(null); load(); }} />}
    </div>
  );
}

const Kpi = ({ k, v, s, dark, mut }) => (
  <div className="kpi">
    <div className="k">{k}</div>
    <div className={`v ${dark ? "dark" : ""} ${mut ? "mut" : ""}`}>{v}</div>
    {s && <div className="s">{s}</div>}
  </div>
);

// -------- Editar Expediente --------
function EditarExpedienteDialog({ exp, cat, onClose, onSaved }) {
  const esGroups = exp.canal === "groups";
  const esSocio = exp.canal === "socio";
  const esDirecto = exp.canal === "directo";
  const [empleados, setEmpleados] = useState([]);
  const [pasajerosTxt, setPasajerosTxt] = useState((exp.pasajeros || []).map((p) => p.nombre || "").join("\n"));
  const [form, setForm] = useState({
    subtipo: exp.subtipo || (esGroups ? "grupo" : esDirecto ? "normal" : ""),
    agente_id: exp.agente_id || "",
    agente_nombre: exp.agente_nombre || "",
    proyecto_nombre: exp.proyecto_nombre || "",
    pax: exp.pax || 1,
    fecha_venta: (exp.fecha_venta || "").slice(0, 10),
    margen_minimo_pct: exp.margen_minimo_pct ?? 0,
    comision_socio_pct: exp.comision_socio_pct ?? 0,
    notas: exp.notas || "",
  });
  useEffect(() => { axios.get(`${API}/employees`).then((r) => setEmpleados(r.data || [])).catch(() => {}); }, []);

  const submit = async () => {
    if (!esGroups && !pasajerosTxt.trim()) { toast.error("Captura al menos un pasajero"); return; }
    const pasajeros = esGroups ? (exp.pasajeros || []) : pasajerosTxt.split("\n").map((n) => n.trim()).filter(Boolean).map((nombre) => ({ nombre }));
    try {
      await axios.patch(`${API}/cabina/expedientes/${exp.id}`, {
        ...form, pasajeros, pax: esGroups ? Number(form.pax) : pasajeros.length,
      });
      toast.success("Expediente actualizado");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="cv" style={{ maxWidth: 780, padding: 0, maxHeight: "90vh", overflow: "auto" }}>
        <div style={{ padding: "16px 22px", borderBottom: "1px solid var(--cv-line)" }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Editar expediente {exp.folio}</h3>
          <p style={{ margin: "2px 0 0", color: "var(--cv-muted)", fontSize: 12 }}>Cliente y canal se fijan al crear.</p>
        </div>
        <div style={{ padding: 22 }}>
          <div className="grid g2">
            <label className="f"><span>Canal (no editable)</span><input value={exp.canal} disabled /></label>
            {esGroups ? (
              <label className="f"><span>Subtipo</span>
                <select value={form.subtipo} onChange={(e) => setForm({ ...form, subtipo: e.target.value })}>
                  <option value="grupo">Grupo</option>
                  <option value="evento">Evento</option>
                  <option value="fit">FIT</option>
                </select>
              </label>
            ) : esDirecto ? (
              <label className="f"><span>Subtipo</span>
                <select value={form.subtipo} onChange={(e) => setForm({ ...form, subtipo: e.target.value })}>
                  <option value="normal">Normal</option>
                  <option value="friends_family">Friends &amp; Family</option>
                </select>
              </label>
            ) : <div />}
            <label className="f" style={{ gridColumn: "1 / -1" }}><span>Cliente (no editable)</span><input value={exp.cliente_nombre} disabled /></label>
            <label className="f" style={{ gridColumn: "1 / -1" }}><span>Agente</span>
              <select value={form.agente_id} onChange={(e) => {
                const eid = e.target.value;
                const emp = empleados.find((x) => x.id === eid);
                setForm({ ...form, agente_id: eid, agente_nombre: emp?.nombre || form.agente_nombre });
              }}>
                <option value="">— Selecciona un empleado —</option>
                {empleados.map((emp) => <option key={emp.id} value={emp.id}>{emp.nombre}{emp.puesto ? ` · ${emp.puesto}` : ""}</option>)}
                {form.agente_nombre && !empleados.some((emp) => emp.id === form.agente_id) && (
                  <option value="" disabled>Actual: {form.agente_nombre} (no vinculado)</option>
                )}
              </select>
            </label>
            {esGroups && (
              <>
                <label className="f"><span>PAX confirmados</span><input type="number" value={form.pax ?? ""} onChange={(e) => setForm({ ...form, pax: e.target.value === "" ? "" : Number(e.target.value) })} min={1} /></label>
                <label className="f"><span>Nombre del proyecto</span><input value={form.proyecto_nombre} onChange={(e) => setForm({ ...form, proyecto_nombre: e.target.value })} /></label>
              </>
            )}
            {!esGroups && (
              <label className="f" style={{ gridColumn: "1 / -1" }}><span>Pasajeros (uno por renglón)</span>
                <textarea rows={3} value={pasajerosTxt} onChange={(e) => setPasajerosTxt(e.target.value)} />
                <div className="hint">PAX se recalcula al guardar.</div>
              </label>
            )}
            <label className="f"><span>Fecha de venta</span><input type="date" value={form.fecha_venta} onChange={(e) => setForm({ ...form, fecha_venta: e.target.value })} /></label>
            <label className="f"><span>Margen mínimo de la venta %</span><input type="number" value={form.margen_minimo_pct ?? ""} onChange={(e) => setForm({ ...form, margen_minimo_pct: e.target.value === "" ? "" : Number(e.target.value) })} /></label>
            {esSocio && <label className="f"><span>Comisión socio %</span><input type="number" value={form.comision_socio_pct ?? ""} onChange={(e) => setForm({ ...form, comision_socio_pct: e.target.value === "" ? "" : Number(e.target.value) })} /></label>}
            <label className="f" style={{ gridColumn: "1 / -1" }}><span>Notas</span><textarea rows={2} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} /></label>
          </div>
        </div>
        <div className="dlg-foot">
          <button className="btn ghost sm" onClick={onClose}>Cancelar</button>
          <button className="btn sm" onClick={submit} data-testid="btn-guardar-expediente">Guardar cambios</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// -------- Proveedor Combobox (usa /api/proveedores-gasto del backoffice) --------
function ProveedorCombobox({ value, onSelect }) {
  const [q, setQ] = useState(value || "");
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    axios.get(`${API}/proveedores-gasto`).then((r) => setList(r.data || [])).catch(() => setList([]));
  }, []);
  useEffect(() => { setQ(value || ""); }, [value]);
  const filtered = q.trim().length >= 1 ? list.filter((p) => (p.nombre_comercial || p.razon_social || "").toLowerCase().includes(q.toLowerCase())).slice(0, 8) : list.slice(0, 8);
  return (
    <div className="combo-wrap">
      <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} placeholder="Escribe para buscar proveedor" data-testid="proveedor-combobox" />
      {open && filtered.length > 0 && (
        <div className="combo-list">
          {filtered.map((p) => (
            <div key={p.id} className="combo-item" onMouseDown={(e) => { e.preventDefault(); setQ(p.nombre_comercial || p.razon_social); onSelect(p); setOpen(false); }}>
              <b>{p.nombre_comercial || p.razon_social}</b>
              <small>{[p.razon_social !== p.nombre_comercial ? p.razon_social : null, p.rfc].filter(Boolean).join(" · ")}</small>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// -------- Add Servicio --------
function AddServicioDialog({ exp, cat, onClose, onSaved }) {
  const [form, setForm] = useState({
    categoria_codigo: "", descripcion: "", localizador: "",
    proveedor_id: null, proveedor_nombre: "",
    cantidad: 1, ambito_fiscal: "nacional", venta_total_mxn: 0,
    fecha_inicio_servicio: "", fecha_fin_servicio: "",
    origen: null, destino: null,
    ambito_fiscal_auto: true,
  });
  const catActual = (cat?.categorias || []).find((c) => c.codigo === form.categoria_codigo);
  const ambitos = catActual?.ambitos || ["nacional"];
  const catsCanal = (cat?.categorias || []).filter((c) => (c.canales || []).includes(exp.canal));
  const gruposCat = cat?.grupos_categoria || [];
  const gruposMeta = Object.fromEntries(gruposCat.map((g) => [g.codigo, g]));
  const grupos = useMemo(() => {
    const g = {};
    catsCanal.forEach((c) => { (g[c.grupo] = g[c.grupo] || []).push(c); });
    // Ordenar según `orden` del catálogo de grupos
    const ordered = {};
    Object.keys(g)
      .sort((a, b) => (gruposMeta[a]?.orden ?? 999) - (gruposMeta[b]?.orden ?? 999))
      .forEach((k) => { ordered[k] = g[k]; });
    return ordered;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catsCanal, gruposCat]);
  // Routing según categoría (traído del backend)
  const routing = cat?.routing_por_categoria?.[form.categoria_codigo] || {};
  const showOrigen = !!routing.origen;
  const showDestino = !!routing.destino;

  // Auto-derivar ámbito fiscal desde el país del origen/destino
  useEffect(() => {
    if (!form.ambito_fiscal_auto) return;
    const paisOrigen = (form.origen?.country || "").toUpperCase();
    const paisDestino = (form.destino?.country || "").toUpperCase();
    const esMx = (c) => !c || c === "MX" || c === "MEX" || c.includes("MEXIC") || c.includes("MÉXIC");
    let nuevo = "nacional";
    if (showOrigen && showDestino) {
      const oMx = esMx(paisOrigen), dMx = esMx(paisDestino);
      if (oMx && dMx) nuevo = "nacional";
      else if (oMx && !dMx) nuevo = "internacional_sale_mx";
      else if (!oMx && dMx) nuevo = "internacional_sale_mx";
      else nuevo = "extranjero";
    } else if (showDestino) {
      nuevo = esMx(paisDestino) ? "nacional" : "extranjero";
    }
    // Sólo aplicar si es uno de los ámbitos permitidos por la categoría
    if (ambitos.includes(nuevo) && nuevo !== form.ambito_fiscal) {
      setForm((f) => ({ ...f, ambito_fiscal: nuevo }));
    }
  }, [form.origen, form.destino, form.ambito_fiscal_auto, form.categoria_codigo, showOrigen, showDestino, ambitos, form.ambito_fiscal]);

  const submit = async () => {
    if (!form.categoria_codigo) { toast.error("Selecciona categoría"); return; }
    if (!catActual?.es_costo_sin_venta && !form.venta_total_mxn) { toast.error("Ingresa venta"); return; }
    if (showOrigen && !form.origen) { toast.error("Selecciona el origen"); return; }
    if (showDestino && !form.destino) { toast.error("Selecciona el destino"); return; }
    const payload = {
      ...form,
      origen_codigo: form.origen?.codigo || "", origen_nombre: form.origen?.nombre || "", origen_tipo: form.origen?.tipo || "",
      destino_codigo: form.destino?.codigo || "", destino_nombre: form.destino?.nombre || "", destino_tipo: form.destino?.tipo || "",
    };
    delete payload.origen; delete payload.destino;
    try { await axios.post(`${API}/cabina/expedientes/${exp.id}/servicios`, payload); toast.success("Servicio agregado"); onSaved(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="cv max-w-xl p-0" style={{ padding: 0, maxHeight: "90vh", overflow: "auto" }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--cv-line)" }}><h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Agregar servicio</h3></div>
        <div style={{ padding: 20 }}>
          <div className="grid g2">
            <label className="f" style={{ gridColumn: "1 / -1" }}><span>Categoría</span>
              <select value={form.categoria_codigo} onChange={(e) => setForm({ ...form, categoria_codigo: e.target.value, ambito_fiscal: "nacional", origen: null, destino: null })} data-testid="add-serv-categoria">
                <option value="">Elige categoría</option>
                {Object.entries(grupos).map(([g, arr]) => (
                  <optgroup key={g} label={`${g} — ${gruposMeta[g]?.nombre || g}`}>
                    {arr.map((c) => <option key={c.codigo} value={c.codigo}>{c.nombre}</option>)}
                  </optgroup>
                ))}
              </select>
            </label>
            <label className="f" style={{ gridColumn: "1 / -1" }}><span>Descripción</span><input value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} /></label>
            <label className="f"><span>Localizador / PNR</span><input value={form.localizador} onChange={(e) => setForm({ ...form, localizador: e.target.value.toUpperCase() })} /></label>
            <label className="f"><span>Proveedor (del catálogo)</span>
              <ProveedorCombobox value={form.proveedor_nombre} onSelect={(p) => setForm({ ...form, proveedor_id: p.id, proveedor_nombre: p.nombre_comercial || p.razon_social })} />
            </label>

            {/* Origen / Destino según categoría */}
            {showOrigen && (
              <label className="f"><span>Origen{routing.origen === "airport" ? " (aeropuerto)" : " (destino)"}</span>
                <LugarAutocomplete kind={routing.origen} value={form.origen} onSelect={(v) => setForm({ ...form, origen: v })} onClear={() => setForm({ ...form, origen: null })} testid="serv-origen" />
              </label>
            )}
            {showDestino && (
              <label className="f"><span>Destino{routing.destino === "airport" ? " (aeropuerto)" : " (destino)"}</span>
                <LugarAutocomplete kind={routing.destino} value={form.destino} onSelect={(v) => setForm({ ...form, destino: v })} onClear={() => setForm({ ...form, destino: null })} testid="serv-destino" />
              </label>
            )}

            {(showOrigen || showDestino) && ambitos.length > 1 && (
              <label className="f" style={{ gridColumn: "1 / -1" }}><span>Ámbito fiscal
                <button type="button" onClick={() => setForm({ ...form, ambito_fiscal_auto: !form.ambito_fiscal_auto })}
                  style={{ marginLeft: 8, fontSize: 10.5, background: "transparent", border: "1px solid var(--cv-line)", padding: "1px 6px", borderRadius: 4, cursor: "pointer", color: form.ambito_fiscal_auto ? "var(--cv-blue)" : "var(--cv-muted)" }}
                  data-testid="btn-ambito-auto">
                  {form.ambito_fiscal_auto ? "Auto ✓" : "Manual"}
                </button>
              </span>
                <select value={form.ambito_fiscal} onChange={(e) => setForm({ ...form, ambito_fiscal: e.target.value, ambito_fiscal_auto: false })} disabled={form.ambito_fiscal_auto} data-testid="serv-ambito">
                  {ambitos.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
                {form.ambito_fiscal_auto && <div className="hint">Se calcula del país del origen/destino. Click &quot;Auto&quot; para editar manualmente.</div>}
              </label>
            )}
            {!(showOrigen || showDestino) && ambitos.length > 1 && (
              <label className="f"><span>Ámbito fiscal</span>
                <select value={form.ambito_fiscal} onChange={(e) => setForm({ ...form, ambito_fiscal: e.target.value })}>
                  {ambitos.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </label>
            )}

            <label className="f"><span>Cantidad</span><input type="number" value={form.cantidad ?? ""} onChange={(e) => setForm({ ...form, cantidad: e.target.value === "" ? "" : Number(e.target.value) })} /></label>
            {!catActual?.es_costo_sin_venta && <label className="f" style={{ gridColumn: "1 / -1" }}><span>Venta total al cliente (MXN)</span><input type="number" value={form.venta_total_mxn ?? ""} onChange={(e) => setForm({ ...form, venta_total_mxn: e.target.value === "" ? "" : Number(e.target.value) })} data-testid="add-serv-venta" /></label>}
            <label className="f"><span>Inicio servicio</span><input type="date" value={form.fecha_inicio_servicio} onChange={(e) => setForm({ ...form, fecha_inicio_servicio: e.target.value })} data-testid="serv-fecha-ini" /></label>
            <label className="f"><span>Fin servicio</span><input type="date" value={form.fecha_fin_servicio} onChange={(e) => setForm({ ...form, fecha_fin_servicio: e.target.value })} data-testid="serv-fecha-fin" /></label>
          </div>
        </div>
        <div className="dlg-foot">
          <button className="btn ghost sm" onClick={onClose}>Cancelar</button>
          <button className="btn sm" onClick={submit} data-testid="btn-guardar-servicio">Agregar</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// -------- Captura Costo --------
function CapturaCostoDialog({ serv, cat, onClose, onSaved }) {
  const [costoTotal, setCostoTotal] = useState("");
  const [propuesta, setPropuesta] = useState(null);
  const [desglose, setDesglose] = useState({});
  const [calc, setCalc] = useState(null);
  const [comisionSub, setComisionSub] = useState(0);
  const [comisionIva, setComisionIva] = useState(0);
  const [ajusteMotivo, setAjusteMotivo] = useState("");

  const pl = (cat?.plantillas_costo || []).find((p) => (p.aplica_categorias || []).includes(serv.categoria_codigo))
          || (cat?.plantillas_costo || []).find((p) => p.codigo === "PL-GENERICO");

  // Al abrir el diálogo: si el servicio ya tiene costos guardados (aunque no
  // estén validados), precargamos costo total, desglose y comisión de proveedor
  // para que administración pueda revisar/validar sobre lo que ya se guardó.
  useEffect(() => {
    const desgloseGuardado = serv?.costo_desglose || [];
    if (desgloseGuardado.length > 0) {
      const d = {};
      desgloseGuardado.forEach((it) => { d[it.codigo] = it.monto; });
      setDesglose(d);
      setCostoTotal(serv.costo_total_mxn || Object.values(d).reduce((a, v) => a + Number(v || 0), 0));
      const com = serv.comision_proveedor || {};
      setComisionSub(com.subtotal || 0);
      setComisionIva(com.iva || 0);
      // Traer la propuesta (regla vigente) para mostrar los inputs por componente
      axios.post(`${API}/cabina/proponer-desglose`, {
        categoria_codigo: serv.categoria_codigo, ambito: serv.ambito_fiscal,
        costo_total: Number(serv.costo_total_mxn) || 0,
      }).then((r) => setPropuesta(r.data)).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serv?.id]);

  const proponer = async () => {
    if (!costoTotal) { toast.error("Ingresa costo total"); return; }
    try {
      const r = await axios.post(`${API}/cabina/proponer-desglose`, {
        categoria_codigo: serv.categoria_codigo, ambito: serv.ambito_fiscal, costo_total: Number(costoTotal),
      });
      setPropuesta(r.data);
      const d = {}; Object.entries(r.data.propuesta).forEach(([k, v]) => { d[k] = v; });
      setDesglose(d);
    } catch (e) { toast.error("Error"); }
  };

  const recalc = useCallback(async () => {
    const tiposMap = { tarifa_base: "base_gravada", iva_tarifa_base: "iva_acreditable", tarifa_exenta: "exento_no_objeto",
                       otros_impuestos: "impuesto_no_gravado", ish: "impuesto_no_gravado", propinas: "sin_iva", cargo_servicio: "sin_iva" };
    const list = Object.entries(desglose).filter(([_, v]) => Number(v) > 0)
      .map(([codigo, monto]) => ({ codigo, tipo: tiposMap[codigo] || "base_gravada", monto: Number(monto) }));
    try {
      const r = await axios.post(`${API}/cabina/calcular-fiscal`, {
        venta_total_mxn: serv.venta_total_mxn, desglose: list,
        comision_subtotal: Number(comisionSub), comision_iva: Number(comisionIva),
        es_costo_sin_venta: serv.es_costo_sin_venta,
      });
      setCalc(r.data);
    } catch (e) { /* silent */ }
  }, [desglose, comisionSub, comisionIva, serv]);

  useEffect(() => { if (Object.keys(desglose).length > 0) recalc(); }, [desglose, comisionSub, comisionIva, recalc]);

  const guardar = async () => {
    const list = Object.entries(desglose).filter(([_, v]) => Number(v) > 0).map(([codigo, monto]) => ({ codigo, monto: Number(monto) }));
    if (list.length === 0) { toast.error("Captura el desglose"); return; }
    try {
      await axios.post(`${API}/cabina/servicios/${serv.id}/costo`, {
        costo_desglose: list,
        comision_proveedor: { subtotal: Number(comisionSub), iva: Number(comisionIva), fecha_pago: null },
      });
      toast.success("Costo capturado"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const validar = async () => {
    const ajustado = !!ajusteMotivo;
    try {
      await axios.post(`${API}/cabina/servicios/${serv.id}/validar-fiscal`, { ajustado, ajuste_motivo: ajusteMotivo });
      toast.success("Costo validado"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const cuadra = calc?.cuadra;

  // ===== TAB SERVICIO =====
  const [tab, setTab] = useState("costo");
  const [servForm, setServForm] = useState(null); // se inicializa al abrir el tab Servicio
  const [savingServ, setSavingServ] = useState(false);

  const initServForm = () => {
    setServForm({
      descripcion: serv.descripcion || "",
      localizador: serv.localizador || "",
      proveedor_id: serv.proveedor_id || "",
      proveedor_nombre: serv.proveedor_nombre || "",
      cantidad: serv.cantidad ?? 1,
      venta_total_mxn: serv.venta_total_mxn ?? 0,
      fecha_inicio_servicio: (serv.fecha_inicio_servicio || "").slice(0, 10),
      fecha_fin_servicio: (serv.fecha_fin_servicio || "").slice(0, 10),
      ambito_fiscal: serv.ambito_fiscal || "nacional",
      origen: serv.origen_codigo ? { codigo: serv.origen_codigo, nombre: serv.origen_nombre || "", tipo: serv.origen_tipo || "" } : null,
      destino: serv.destino_codigo ? { codigo: serv.destino_codigo, nombre: serv.destino_nombre || "", tipo: serv.destino_tipo || "" } : null,
    });
  };
  useEffect(() => { initServForm(); /* eslint-disable-next-line */ }, [serv?.id]);

  const catActualServ = (cat?.categorias || []).find((c) => c.codigo === serv.categoria_codigo);
  const ambitosServ = catActualServ?.ambitos || ["nacional"];
  const routingServ = cat?.routing_por_categoria?.[serv.categoria_codigo] || {};

  const guardarServicio = async () => {
    if (!servForm) return;
    setSavingServ(true);
    try {
      const payload = {
        descripcion: servForm.descripcion,
        localizador: servForm.localizador,
        proveedor_id: servForm.proveedor_id || null,
        proveedor_nombre: servForm.proveedor_nombre,
        cantidad: Number(servForm.cantidad) || 1,
        venta_total_mxn: Number(servForm.venta_total_mxn) || 0,
        fecha_inicio_servicio: servForm.fecha_inicio_servicio,
        fecha_fin_servicio: servForm.fecha_fin_servicio,
        ambito_fiscal: servForm.ambito_fiscal,
        origen_codigo: servForm.origen?.codigo || "",
        origen_nombre: servForm.origen?.nombre || "",
        origen_tipo: servForm.origen?.tipo || "",
        destino_codigo: servForm.destino?.codigo || "",
        destino_nombre: servForm.destino?.nombre || "",
        destino_tipo: servForm.destino?.tipo || "",
      };
      await axios.patch(`${API}/cabina/servicios/${serv.id}`, payload);
      toast.success("Servicio actualizado");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar servicio");
    } finally {
      setSavingServ(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="cv" style={{ maxWidth: 900, padding: 0, maxHeight: "90vh", overflow: "auto" }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--cv-line)" }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Servicio <b style={{ color: "var(--cv-blue)" }}>{serv.folio}</b> · {serv.categoria_nombre}</h3>
          <p style={{ margin: "4px 0 0", color: "var(--cv-muted)", fontSize: 12.5 }}>Ámbito: <b>{serv.ambito_fiscal}</b> · Venta: <b className="mono">{fmt(serv.venta_total_mxn)}</b></p>
          {/* Tabs */}
          <div style={{ display: "flex", gap: 4, marginTop: 12, borderBottom: "1px solid var(--cv-line)", marginBottom: -1 }}>
            <button
              className="tab-btn"
              onClick={() => setTab("servicio")}
              data-testid="tab-servicio"
              style={{ background: "none", border: 0, padding: "8px 16px", cursor: "pointer",
                fontSize: 13, fontWeight: 700,
                borderBottom: tab === "servicio" ? "2px solid var(--cv-blue)" : "2px solid transparent",
                color: tab === "servicio" ? "var(--cv-blue)" : "var(--cv-muted)" }}>
              Servicio
            </button>
            <button
              className="tab-btn"
              onClick={() => setTab("costo")}
              data-testid="tab-costo"
              style={{ background: "none", border: 0, padding: "8px 16px", cursor: "pointer",
                fontSize: 13, fontWeight: 700,
                borderBottom: tab === "costo" ? "2px solid var(--cv-blue)" : "2px solid transparent",
                color: tab === "costo" ? "var(--cv-blue)" : "var(--cv-muted)" }}>
              Costo
            </button>
          </div>
        </div>

        {tab === "servicio" && servForm && (
          <div style={{ padding: 20 }}>
            <div className="grid g2">
              <label className="f" style={{ gridColumn: "1 / -1" }}><span>Descripción</span>
                <input value={servForm.descripcion} onChange={(e) => setServForm({ ...servForm, descripcion: e.target.value })} data-testid="serv-descripcion" />
              </label>
              <label className="f"><span>Localizador / PNR</span>
                <input value={servForm.localizador} onChange={(e) => setServForm({ ...servForm, localizador: e.target.value.toUpperCase() })} />
              </label>
              <label className="f"><span>Proveedor</span>
                <ProveedorCombobox value={servForm.proveedor_nombre} onSelect={(p) => setServForm({ ...servForm, proveedor_id: p.id, proveedor_nombre: p.nombre_comercial || p.razon_social })} />
              </label>
              {routingServ.origen && (
                <label className="f"><span>Origen ({routingServ.origen === "airport" ? "aeropuerto" : "destino"})</span>
                  <LugarAutocomplete kind={routingServ.origen} value={servForm.origen} onSelect={(v) => setServForm({ ...servForm, origen: v })} onClear={() => setServForm({ ...servForm, origen: null })} testid="edit-serv-origen" />
                </label>
              )}
              {routingServ.destino && (
                <label className="f"><span>Destino ({routingServ.destino === "airport" ? "aeropuerto" : "destino"})</span>
                  <LugarAutocomplete kind={routingServ.destino} value={servForm.destino} onSelect={(v) => setServForm({ ...servForm, destino: v })} onClear={() => setServForm({ ...servForm, destino: null })} testid="edit-serv-destino" />
                </label>
              )}
              {ambitosServ.length > 1 && (
                <label className="f"><span>Ámbito fiscal</span>
                  <select value={servForm.ambito_fiscal} onChange={(e) => setServForm({ ...servForm, ambito_fiscal: e.target.value })} data-testid="edit-serv-ambito">
                    {ambitosServ.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </label>
              )}
              <label className="f"><span>Cantidad</span>
                <input type="number" value={servForm.cantidad ?? ""} onChange={(e) => setServForm({ ...servForm, cantidad: e.target.value === "" ? "" : Number(e.target.value) })} />
              </label>
              {!serv.es_costo_sin_venta && (
                <label className="f"><span>Venta total al cliente</span>
                  <input type="number" value={servForm.venta_total_mxn ?? ""} onChange={(e) => setServForm({ ...servForm, venta_total_mxn: e.target.value === "" ? "" : Number(e.target.value) })} data-testid="edit-serv-venta" />
                </label>
              )}
              <label className="f"><span>Inicio servicio</span>
                <input type="date" value={servForm.fecha_inicio_servicio} onChange={(e) => setServForm({ ...servForm, fecha_inicio_servicio: e.target.value })} />
              </label>
              <label className="f"><span>Fin servicio</span>
                <input type="date" value={servForm.fecha_fin_servicio} onChange={(e) => setServForm({ ...servForm, fecha_fin_servicio: e.target.value })} />
              </label>
            </div>
            {serv.estatus_costo === "validado" && (
              <div className="rulebox" style={{ marginTop: 12, background: "#fef9c3", borderColor: "#fde68a" }}>
                <b>Costo ya validado.</b> Al editar la venta, ámbito o categoría es recomendable re-validar el desglose fiscal en la pestaña Costo.
              </div>
            )}
          </div>
        )}

        {tab === "costo" && (
        <div style={{ padding: 20 }}>
          <div className="grid g3">
            <label className="f" style={{ gridColumn: "span 2" }}><span>Costo total del proveedor</span><input type="number" value={costoTotal ?? ""} onChange={(e) => setCostoTotal(e.target.value)} data-testid="costo-total-input" /></label>
            <div style={{ alignSelf: "end" }}><button className="btn sm" onClick={proponer} data-testid="btn-proponer">Proponer desglose</button></div>
          </div>
          {propuesta && (
            <>
              <div className="rulebox" style={{ marginTop: 14 }}>
                <b>{propuesta.regla.codigo}</b> — {propuesta.regla.nombre}. <span>{propuesta.regla.nota}</span>
              </div>
              <div style={{ marginTop: 14 }}>
                <div className="grid g2">
                  {(pl?.componentes || []).map((c) => (
                    <label key={c.codigo} className="f"><span>{c.nombre}</span><input type="number" value={desglose[c.codigo] ?? ""} onChange={(e) => setDesglose({ ...desglose, [c.codigo]: e.target.value })} /></label>
                  ))}
                </div>
                <div className="grid g2" style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--cv-line)" }}>
                  <label className="f"><span>Comisión del proveedor (subtotal)</span><input type="number" value={comisionSub ?? ""} onChange={(e) => setComisionSub(e.target.value)} /></label>
                  <label className="f"><span>IVA de la comisión</span><input type="number" value={comisionIva ?? ""} onChange={(e) => setComisionIva(e.target.value)} /></label>
                </div>
              </div>
            </>
          )}
          {calc && (
            <>
              <div className="fout" style={{ marginTop: 14 }}>
                <div><div className="k">Costo neto</div><div className="v mut">{fmt(calc.costo_neto)}</div></div>
                <div><div className="k">IVA acreditable</div><div className="v mut">{fmt(calc.iva_acreditable)}</div></div>
                <div><div className="k">Utilidad</div><div className="v hi">{fmt(calc.utilidad_total)}</div></div>
                <div><div className="k">IVA trasladado</div><div className="v mut">{fmt(calc.iva_trasladado)}</div></div>
                <div><div className="k">IVA por pagar</div><div className={`v ${calc.iva_por_pagar < 0 ? "bad" : ""}`}>{fmt(calc.iva_por_pagar)}</div></div>
                <div><div className="k">Margen</div><div className="v hi">{calc.margen_pct}%</div></div>
              </div>
              <div className={`cuadre ${cuadra ? "ok" : "bad"}`}>
                <div><span>Suma del desglose</span><b className="mono">{fmt(calc.cuadre)}</b></div>
                <div><span>Venta</span><b className="mono">{fmt(serv.venta_total_mxn)}</b></div>
                <div className="res">{cuadra ? "Cuadra ✓" : "No cuadra — ajusta el desglose"}</div>
              </div>
              {!cuadra && (
                <label className="f" style={{ marginTop: 12 }}><span>Motivo del ajuste (obligatorio para validar)</span><textarea rows={2} value={ajusteMotivo} onChange={(e) => setAjusteMotivo(e.target.value)} placeholder="ISH separado, propina no gravada, cargo duplicado..." /></label>
              )}
            </>
          )}
        </div>
        )}
        <div className="dlg-foot">
          <button className="btn ghost sm" onClick={onClose}>Cancelar</button>
          {tab === "servicio" ? (
            <button className="btn dark sm" onClick={guardarServicio} disabled={savingServ} data-testid="btn-guardar-servicio-edit">Guardar servicio</button>
          ) : (
            <>
              <button className="btn ghost sm" onClick={guardar} data-testid="btn-guardar-costo">Guardar sin validar</button>
              <button className="btn dark sm" onClick={validar} disabled={!calc || (!cuadra && !ajusteMotivo)} data-testid="btn-validar-costo">Validar fiscal ✓</button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// -------- Registrar CFDI --------
function RegistrarFacturaDialog({ exp, cat, onClose, onSaved }) {
  const [clienteData, setClienteData] = useState(null);
  const [form, setForm] = useState({
    folio: "", uuid: "", fecha: new Date().toISOString().slice(0, 10),
    subtotal: exp.totales?.subtotal_estimado || 0,
    iva: exp.totales?.iva_estimado || 0,
    estatus_pago: "pendiente", forma: "99", fecha_pago: "", referencia: "",
    dias_credito: 0,
  });
  // Cargar días de crédito default del cliente
  useEffect(() => {
    if (!exp.cliente_id) return;
    axios.get(`${API}/clients/${exp.cliente_id}`).then((r) => {
      setClienteData(r.data);
      setForm((f) => ({ ...f, dias_credito: r.data?.dias_credito ?? 0 }));
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const total = Number(form.subtotal || 0) + Number(form.iva || 0);
  // Fecha de vencimiento derivada
  const fechaVenc = useMemo(() => {
    const d = Number(form.dias_credito || 0);
    if (!form.fecha || d <= 0) return "";
    try { const dt = new Date(form.fecha + "T00:00:00"); dt.setDate(dt.getDate() + d); return dt.toISOString().slice(0, 10); }
    catch { return ""; }
  }, [form.fecha, form.dias_credito]);
  const submit = async () => {
    if (!form.subtotal || Number(form.subtotal) <= 0) { toast.error("Subtotal requerido"); return; }
    try { await axios.post(`${API}/cabina/expedientes/${exp.id}/facturas`, form); toast.success("Factura registrada"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  // PPD → forma siempre "99" (Por definir). PUE → cualquier forma excepto 99.
  const esPPD = form.estatus_pago === "pendiente";
  const formasList = (cat?.formas_pago_sat || []).filter((f) => (esPPD ? (f.clave || f.codigo) === "99" : (f.clave || f.codigo) !== "99"));
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="cv" style={{ maxWidth: 620, padding: 0, maxHeight: "90vh", overflow: "auto" }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--cv-line)" }}><h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Registrar CFDI</h3></div>
        <div style={{ padding: 20 }}>
          <div className="grid g2">
            <label className="f"><span>Folio factura ERP</span><input value={form.folio} onChange={(e) => setForm({ ...form, folio: e.target.value })} data-testid="input-folio-factura" placeholder="Ej. F-2026-0123" /></label>
            <label className="f"><span>Fecha</span><input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} /></label>
            <label className="f" style={{ gridColumn: "1 / -1" }}>
              <span>UUID (opcional)</span>
              <input value={form.uuid} onChange={(e) => setForm({ ...form, uuid: e.target.value.trim() })} placeholder="00000000-0000-0000-0000-000000000000" data-testid="input-uuid" />
              <div className="hint">UUID fiscal del CFDI. Si aún no lo tienes, déjalo vacío y captúralo después.</div>
            </label>
            <label className="f"><span>Subtotal</span><input type="number" value={form.subtotal ?? ""} onChange={(e) => setForm({ ...form, subtotal: e.target.value === "" ? "" : Number(e.target.value) })} /></label>
            <label className="f"><span>IVA</span><input type="number" value={form.iva ?? ""} onChange={(e) => setForm({ ...form, iva: e.target.value === "" ? "" : Number(e.target.value) })} /></label>
            <div style={{ gridColumn: "1 / -1", padding: "8px 10px", background: "#f7f9ff", borderRadius: 8, fontSize: 13 }}>Total: <b className="mono">{fmt(total)}</b></div>
            <label className="f"><span>Estatus de pago</span>
              <select value={form.estatus_pago} onChange={(e) => setForm({ ...form, estatus_pago: e.target.value, forma: e.target.value === "pagada" ? "" : "99" })}>
                <option value="pendiente">Pendiente (PPD)</option>
                <option value="pagada">Pagada (PUE)</option>
              </select>
            </label>
            <label className="f"><span>Forma de pago SAT</span>
              {esPPD ? (
                <input value="99 Por definir" disabled data-testid="forma-sat-disabled" />
              ) : (
                <select value={form.forma} onChange={(e) => setForm({ ...form, forma: e.target.value })} data-testid="select-forma-sat">
                  <option value="">Elige</option>
                  {formasList.map((f) => <option key={f.clave || f.codigo} value={f.clave || f.codigo}>{f.clave || f.codigo} {f.nombre}</option>)}
                </select>
              )}
            </label>
            <label className="f"><span>Días de crédito</span>
              <input type="number" value={form.dias_credito ?? ""} onChange={(e) => setForm({ ...form, dias_credito: e.target.value === "" ? "" : Number(e.target.value) })} data-testid="input-dias-credito" />
              <div className="hint">Default del cliente: {clienteData?.dias_credito ?? 0} días.{fechaVenc ? ` Vence el ${fechaVenc}.` : ""}</div>
            </label>
            {form.estatus_pago === "pagada" && <label className="f"><span>Fecha del pago</span><input type="date" value={form.fecha_pago} onChange={(e) => setForm({ ...form, fecha_pago: e.target.value })} /></label>}
          </div>
        </div>
        <div className="dlg-foot">
          <button className="btn ghost sm" onClick={onClose}>Cancelar</button>
          <button className="btn sm" onClick={submit} data-testid="btn-guardar-cfdi">Registrar</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// -------- Registrar Pago --------
function RegistrarPagoDialog({ factura, cat, onClose, onSaved }) {
  const cobrado = (factura.pagos || []).reduce((a, p) => a + p.importe, 0);
  const saldo = factura.total - cobrado;
  const [form, setForm] = useState({ fecha: new Date().toISOString().slice(0, 10), importe: saldo, forma: "", medio_pago: "", referencia: "" });
  const submit = async () => {
    if (form.importe <= 0 || form.importe > saldo + 0.5) { toast.error("Importe inválido"); return; }
    if (!form.forma) { toast.error("Elige forma de pago"); return; }
    try { await axios.post(`${API}/cabina/facturas/${factura.id}/pagos`, form); toast.success("Pago registrado"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="cv" style={{ maxWidth: 520, padding: 0, maxHeight: "90vh", overflow: "auto" }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--cv-line)" }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Registrar pago</h3>
          <p style={{ margin: "4px 0 0", color: "var(--cv-muted)", fontSize: 12.5 }}>Factura {factura.folio || "s/f"} · saldo <b className="mono">{fmt(saldo)}</b></p>
        </div>
        <div style={{ padding: 20 }}>
          <div className="grid g2">
            <label className="f"><span>Fecha</span><input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} /></label>
            <label className="f"><span>Importe</span><input type="number" value={form.importe ?? ""} onChange={(e) => setForm({ ...form, importe: e.target.value === "" ? "" : Number(e.target.value) })} data-testid="pago-importe" /></label>
            <label className="f"><span>Forma de pago SAT</span>
              <select value={form.forma} onChange={(e) => setForm({ ...form, forma: e.target.value })} data-testid="pago-forma">
                <option value="">Elige</option>
                {(cat?.formas_pago_sat || []).filter((f) => (f.clave || f.codigo) !== "99").map((f) => <option key={f.clave || f.codigo} value={f.clave || f.codigo}>{f.clave || f.codigo} {f.nombre}</option>)}
              </select>
            </label>
            <label className="f"><span>Medio de pago (banco)</span>
              <select value={form.medio_pago} onChange={(e) => setForm({ ...form, medio_pago: e.target.value })} data-testid="pago-medio">
                <option value="">— Elige —</option>
                {(cat?.medios_pago || []).map((m) => <option key={m.codigo} value={m.codigo}>{m.nombre}{m.pct_pasarela > 0 ? ` · pasarela ${(m.pct_pasarela * 100).toFixed(2)}%` : ""}</option>)}
              </select>
              <div className="hint">Por dónde entra el dinero (BBVA MXN, Stripe, PayPal...). Es info interna, no fiscal.</div>
            </label>
            <label className="f" style={{ gridColumn: "1 / -1" }}><span>Referencia</span><input value={form.referencia} onChange={(e) => setForm({ ...form, referencia: e.target.value })} /></label>
          </div>
        </div>
        <div className="dlg-foot">
          <button className="btn ghost sm" onClick={onClose}>Cancelar</button>
          <button className="btn dark sm" onClick={submit} data-testid="btn-guardar-pago">Registrar pago</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
